import { memo, useEffect, useRef, useState } from 'react';
import { addDays } from 'date-fns';
import { toDate, toZonedTime } from 'date-fns-tz';
import { FormApi } from 'informed';
import { useDateNow, useReduxState } from 'hooks';
import { Button, FormattedDateWithTime } from 'components/common';
import { Dropdown } from 'components/common/dropdown';
import { DatepickerWithTime, Form, RadioGroup } from 'components/common/form';
import { editorSocket } from 'components/editor/socket';

import type { EditorHeaderStateProps } from './EditorHeader';

type FormValues = {
  published: string;
  publishDate: number;
};

type Props = Pick<
  EditorHeaderStateProps,
  'currentChapter' | 'isPublishing' | 'previewType' | 'publishDate'
>;

const EditorHeaderPublish = ({
  currentChapter,
  isPublishing,
  previewType,
  publishDate
}: Props) => {
  const formApi = useRef<FormApi<FormValues>>(undefined);

  const { dateNow } = useDateNow();

  const { messages, serverConfig, theme } = useReduxState();

  const getInitialValues = (now: number) => {
    return {
      published: !publishDate
        ? 'no'
        : Date.parse(publishDate) > now
        ? 'later'
        : 'yes',
      publishDate: publishDate
        ? Date.parse(toZonedTime(publishDate, serverConfig.timeZone).toString())
        : null
    };
  };

  const [initialValues, setInitialValues] = useState(getInitialValues(dateNow));

  useEffect(() => {
    // click outside dropdown to close it
    if (!isPublishing) document.body.click();
  }, [isPublishing]);

  useEffect(() => {
    setInitialValues({ ...getInitialValues(Date.now()) });
  }, [publishDate]);

  const handlePublishedChange = (value) => {
    const initialPublishDate = initialValues.publishDate;
    if (value === 'no') formApi.current.setValue('publishDate', null);
    else if (value === 'yes') {
      if (initialPublishDate && initialPublishDate <= Date.now())
        formApi.current.setValue('publishDate', initialPublishDate);
      else formApi.current.setValue('publishDate', null);
    } else {
      if (initialPublishDate && initialPublishDate > Date.now())
        formApi.current.setValue('publishDate', initialPublishDate);
      else
        formApi.current.setValue(
          'publishDate',
          Date.parse(addDays(Date.now(), 1).toString())
        );
    }
  };

  const submit = () => {
    const values = formApi.current.getState().values;

    const published = values.published;
    let publishDate: number = values.publishDate || null;
    if (published === 'yes' && !publishDate) publishDate = Date.now();
    else if (published === 'later' && publishDate !== initialValues.publishDate)
      publishDate = Date.parse(
        toDate(publishDate, { timeZone: serverConfig.timeZone }).toString()
      );

    editorSocket.publish({ chapterId: currentChapter.id, publishDate });
  };

  const buttonStyle = {
    backgroundColor: 'transparent',
    color:
      initialValues.published === 'no'
        ? theme.dangerColor
        : initialValues.published === 'yes'
        ? theme.successStandaloneColor
        : theme.textColorLight,
    fontSize: '1.3rem',
    fontWeight: '600',
    padding: 0
  };

  return (
    <div className="publish" data-keep-open={true}>
      {previewType ? (
        <div style={buttonStyle}>
          {initialValues.published === 'no' ? (
            messages.commonNotPublished
          ) : initialValues.published === 'yes' ? (
            messages.commonPublished
          ) : (
            <FormattedDateWithTime
              inputDate={new Date(initialValues.publishDate).toString()}
              text={messages.commonWillPublish}
            />
          )}
        </div>
      ) : (
        <Dropdown
          arrowStyle={{ marginLeft: 5 }}
          buttonStyle={buttonStyle}
          contentStyle={{ overflow: 'visible', top: -10 }}
          showArrow={true}
          styleContentButtons={false}
          title={
            initialValues.published === 'no' ? (
              messages.commonNotPublished
            ) : initialValues.published === 'yes' ? (
              messages.commonPublished
            ) : (
              <FormattedDateWithTime
                inputDate={new Date(initialValues.publishDate).toString()}
                text={messages.commonWillPublish}
              />
            )
          }
        >
          <Form
            getApi={(ref) => (formApi.current = ref)}
            initialValues={initialValues}
            onSubmit={submit}
          >
            {({ formState }) => (
              <div className="content">
                <RadioGroup
                  field="published"
                  onChange={handlePublishedChange}
                  options={[
                    {
                      id: 'header-published-no',
                      label: messages.commonNotPublished,
                      value: 'no'
                    },
                    {
                      id: 'header-published-yes',
                      label: messages.commonPublished,
                      value: 'yes'
                    },
                    {
                      id: 'header-published-later',
                      label: messages.publishPublishLater,
                      value: 'later'
                    }
                  ]}
                />

                <DatepickerWithTime
                  additionalCalendarClassName="data-keep-open"
                  direction="column"
                  disabled={formState.values.published !== 'later'}
                  field="publishDate"
                  id="publish-date"
                  isClearable={false}
                  minDate={addDays(dateNow, 1)}
                  placeholder={
                    formState.values.published === 'no'
                      ? messages.publishStartDateEmpty
                      : messages.publishNow
                  }
                  style={{ marginTop: 10, width: 200 }}
                  usePortal={true}
                  {...(!initialValues.publishDate
                    ? { initialTime: '08:00' }
                    : {})}
                />

                <div className="footer">
                  <Button
                    category="success"
                    loading={isPublishing}
                    onClick={submit}
                    text={messages.commonSave}
                    type="submit"
                  />
                </div>
              </div>
            )}
          </Form>
        </Dropdown>
      )}

      <style jsx>{`
        .publish {
          position: relative;
        }
        .publish :global(.toggle:hover),
        .publish :global(.toggle:focus) {
          text-decoration: underline;
        }
        .content {
          font-style: normal;
          padding: 10px 15px;
        }
        .footer {
          margin-top: 15px;
          text-align: right;
        }
      `}</style>
    </div>
  );
};

export default memo(EditorHeaderPublish);
